import React, { useState, useMemo } from 'react';
import { Globe, Building2, Users, AlertTriangle, Loader2, TrendingUp, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const numStyle = { fontFamily: 'Manrope, sans-serif' };

function SummaryCard({ icon: Icon, label, value, iconColor = 'text-stone-500', bgTint = 'bg-white', onClick, isActive }) {
  return (
    <button
      onClick={onClick}
      className={`${bgTint} rounded-lg border p-4 shadow-sm text-left transition-all w-full ${
        isActive
          ? 'border-stone-900 ring-2 ring-stone-900/20 shadow-md'
          : 'border-stone-200 hover:border-stone-300 hover:shadow-md'
      } ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${iconColor}`} />
        <span className="text-xs font-medium text-stone-500 uppercase tracking-wide">{label}</span>
        {isActive && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-900 text-white font-medium ml-auto">Active</span>
        )}
      </div>
      <p className="text-2xl font-semibold text-stone-900" style={numStyle}>
        {value?.toLocaleString() ?? 0}
      </p>
    </button>
  );
}

function BalanceBar({ value, max, isHigh, isLow }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  let barColor = 'bg-stone-400';
  if (isHigh) barColor = 'bg-amber-500';
  if (isLow) barColor = 'bg-blue-400';

  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-stone-400 w-8 text-right" style={numStyle}>{pct}%</span>
    </div>
  );
}

export default function StagingDashboard({ dashboard, loading, onFilterChange, activeFilters, onCardClick, activeView }) {
  const filters = dashboard?.filters || {};
  const summary = dashboard?.summary || {};
  const reps = dashboard?.reps || [];

  // Show all reps so the full team is visible for assignment planning
  const activeReps = reps;

  const { avg, max } = useMemo(() => {
    if (activeReps.length === 0) return { avg: 0, max: 0 };
    const totals = activeReps.map(
      (r) => (r.crm_contacts || 0) + (r.pending_contacts || 0)
    );
    const sum = totals.reduce((a, b) => a + b, 0);
    return {
      avg: sum / totals.length,
      max: Math.max(...totals),
    };
  }, [activeReps]);

  const handleFilterChange = (key, value) => {
    const next = { ...activeFilters };
    next[key] = value === '__all__' ? '' : value;
    // Cascade: clear downstream filters
    if (key === 'region') {
      next.country = '';
      next.state = '';
    }
    if (key === 'country') {
      next.state = '';
    }
    onFilterChange?.(next);
  };

  // Derive filtered country/state lists based on active selections
  const filteredCountries = useMemo(() => {
    if (!filters.countries) return [];
    if (!activeFilters?.region) return filters.countries;
    return filters.countries.filter((c) => c.region === activeFilters.region);
  }, [filters.countries, activeFilters?.region]);

  const filteredStates = useMemo(() => {
    if (!filters.states) return [];
    if (!activeFilters?.country) return filters.states;
    return filters.states.filter((s) => s.country === activeFilters.country);
  }, [filters.states, activeFilters?.country]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
        <span className="ml-2 text-stone-500 text-sm">Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Geographic Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
        <Globe className="h-4 w-4 text-stone-400" />
        <span className="text-sm font-medium text-stone-600 mr-1">Filter by:</span>

        <Select
          value={activeFilters?.region || '__all__'}
          onValueChange={(v) => handleFilterChange('region', v)}
        >
          <SelectTrigger className="w-[160px] h-8 text-sm bg-white">
            <SelectValue placeholder="All Regions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Regions</SelectItem>
            {(filters.regions || []).map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={activeFilters?.country || '__all__'}
          onValueChange={(v) => handleFilterChange('country', v)}
        >
          <SelectTrigger className="w-[180px] h-8 text-sm bg-white">
            <SelectValue placeholder="All Countries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Countries</SelectItem>
            {filteredCountries.map((c) => {
              const val = typeof c === 'string' ? c : c.name || c.value;
              return (
                <SelectItem key={val} value={val}>
                  {val}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <Select
          value={activeFilters?.state || '__all__'}
          onValueChange={(v) => handleFilterChange('state', v)}
        >
          <SelectTrigger className="w-[180px] h-8 text-sm bg-white">
            <SelectValue placeholder="All States / Cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All States / Cities</SelectItem>
            {filteredStates.map((s) => {
              const val = typeof s === 'string' ? s : s.name || s.value;
              return (
                <SelectItem key={val} value={val}>
                  {val}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <SummaryCard
          icon={Building2}
          label="Pending Companies"
          value={summary.total_pending_companies}
          iconColor="text-emerald-500"
          onClick={() => onCardClick?.('pending-companies')}
          isActive={activeView === 'pending-companies'}
        />
        <SummaryCard
          icon={Users}
          label="Pending Contacts"
          value={summary.total_pending_contacts}
          iconColor="text-emerald-500"
          onClick={() => onCardClick?.('pending-contacts')}
          isActive={activeView === 'pending-contacts'}
        />
        <SummaryCard
          icon={Building2}
          label="CRM Companies"
          value={summary.total_crm_accounts}
          iconColor="text-stone-500"
          onClick={() => onCardClick?.('crm-companies')}
          isActive={activeView === 'crm-companies'}
        />
        <SummaryCard
          icon={Users}
          label="CRM Contacts"
          value={summary.total_crm_contacts}
          iconColor="text-stone-500"
          onClick={() => onCardClick?.('crm-contacts')}
          isActive={activeView === 'crm-contacts'}
        />
        <SummaryCard
          icon={AlertTriangle}
          label="Unassigned"
          value={summary.unassigned}
          iconColor="text-amber-500"
          bgTint={activeView === 'unassigned' ? 'bg-amber-100' : 'bg-amber-50'}
          onClick={() => onCardClick?.('unassigned')}
          isActive={activeView === 'unassigned'}
        />
      </div>

      {/* Per-Rep Distribution Table */}
      <RepDistributionCollapsible activeReps={activeReps} avg={avg} max={max} />
    </div>
  );
}

function RepDistributionCollapsible({ activeReps, avg, max }) {
  const [collapsed, setCollapsed] = useState(true);

  return (
      <div className="bg-white rounded-lg border border-stone-200 shadow-sm overflow-hidden">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full px-4 py-3 border-b border-stone-100 flex items-center justify-between hover:bg-stone-50 transition-colors"
        >
          <h3 className="text-sm font-semibold text-stone-700">Rep Distribution ({activeReps.length})</h3>
          <div className="flex items-center gap-2 text-stone-400">
            <span className="text-xs">{collapsed ? 'Show' : 'Hide'}</span>
            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </div>
        </button>

        {!collapsed && (
          activeReps.length === 0 ? (
            <div className="px-4 py-8 text-center text-stone-400 text-sm">
              No reps with data to display.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide">
                    <th className="text-left px-4 py-2 font-medium">Sales Rep</th>
                    <th className="text-center px-3 py-2 font-medium" colSpan={2}>
                      In CRM
                    </th>
                    <th className="text-center px-3 py-2 font-medium" colSpan={2}>
                      + Pending
                    </th>
                    <th className="text-center px-3 py-2 font-medium" colSpan={2}>
                      Total After
                    </th>
                    <th className="text-left px-3 py-2 font-medium">Balance</th>
                  </tr>
                  <tr className="bg-stone-50 text-stone-400 text-[11px] border-b border-stone-200">
                    <th className="px-4 py-1" />
                    <th className="text-center px-3 py-1 font-normal">Companies</th>
                    <th className="text-center px-3 py-1 font-normal">Contacts</th>
                    <th className="text-center px-3 py-1 font-normal">Companies</th>
                    <th className="text-center px-3 py-1 font-normal">Contacts</th>
                    <th className="text-center px-3 py-1 font-normal">Companies</th>
                    <th className="text-center px-3 py-1 font-normal">Contacts</th>
                    <th className="px-3 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {activeReps.map((rep, idx) => {
                    const totalCompanies = (rep.crm_accounts || 0) + (rep.pending_companies || 0);
                    const totalContacts = (rep.crm_contacts || 0) + (rep.pending_contacts || 0);
                    const isHigh = avg > 0 && totalContacts > avg * 1.2;
                    const isLow = avg > 0 && totalContacts < avg * 0.8;

                    let rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-stone-50/50';
                    if (isHigh) rowBg = 'bg-red-50/40';
                    if (isLow) rowBg = 'bg-blue-50/40';

                    return (
                      <tr
                        key={rep.rep_id || rep.name || idx}
                        className={`${rowBg} border-b border-stone-100 last:border-0 hover:bg-stone-100/50 transition-colors`}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-stone-800">{rep.name}</span>
                            {isHigh && <TrendingUp className="h-3.5 w-3.5 text-amber-500" />}
                            {isLow && <TrendingDown className="h-3.5 w-3.5 text-blue-400" />}
                          </div>
                        </td>
                        <td className="text-center px-3 py-2.5 text-stone-600" style={numStyle}>
                          {(rep.crm_accounts || 0).toLocaleString()}
                        </td>
                        <td className="text-center px-3 py-2.5 text-stone-600" style={numStyle}>
                          {(rep.crm_contacts || 0).toLocaleString()}
                        </td>
                        <td className="text-center px-3 py-2.5 text-emerald-600" style={numStyle}>
                          {rep.pending_companies > 0 ? `+${rep.pending_companies.toLocaleString()}` : '0'}
                        </td>
                        <td className="text-center px-3 py-2.5 text-emerald-600" style={numStyle}>
                          {rep.pending_contacts > 0 ? `+${rep.pending_contacts.toLocaleString()}` : '0'}
                        </td>
                        <td className="text-center px-3 py-2.5 font-semibold text-stone-900" style={numStyle}>
                          {totalCompanies.toLocaleString()}
                        </td>
                        <td className="text-center px-3 py-2.5 font-semibold text-stone-900" style={numStyle}>
                          {totalContacts.toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5">
                          <BalanceBar value={totalContacts} max={max} isHigh={isHigh} isLow={isLow} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
  );
}
